<template>
  <div
    id="vessel-position-history-timeline-controller"
    class="h-full flex flex-col"
  >
    <vs-row>
      <ul id="timeline-mode-label" class="in-vessel-view">
        <li>
          <vs-radio
            :color="radioColor"
            v-model="playMode"
            vs-name="live"
            vs-value="live"
            class="live-mode-text"
            >Live</vs-radio
          >
        </li>
        <li>
          <vs-radio
            :color="radioColor"
            v-model="playMode"
            vs-name="history"
            vs-value="history"
            class="history-mode-text"
            >日付選択
          </vs-radio>
        </li>
        <li>
          <feather-icon
            @click="onClickCalendar('history')"
            icon="CalendarIcon"
            svgClasses="h-6 w-6"
            class="cursor-pointer history-mode-calendar"
          />
        </li>
      </ul>
    </vs-row>


    <div class="timeline-slider-parent">
      <div class="timeline-slider-items flex justify-center items-center h-full">
        <button
          class="timeline-control-button cursor-pointer mr-auto ml-4"
          @click="onClickPrev"
          v-if="!isLiveMode"
          :disabled="isSliderMin"
        >
          <feather-icon
            icon="ArrowLeftCircleIcon"
          />
        </button>
        <vue-slider
          id="timeline-slider"
          v-model="sliderValue"
          :marks="sliderMarks"
          :max="144"
          :fixed="true"
          :tooltip-formatter="formatter"
          :tooltip-placement="'bottom'"
          :disabled="sliderDisabled"
          @drag-start="onDragStart"
          @dragging="onDragging"
          @drag-end="onDragEnd"
          @change="onChange"
          class="slider-child"
        >
          <template v-slot:dot>
            <div class="custom-dot" />
          </template>
        </vue-slider>
        <button
          class="timeline-control-button cursor-pointer ml-auto mr-4"
          @click="onClickNext"
          v-if="!isLiveMode"
          :disabled="isSliderMax"
        >
          <feather-icon
            icon="ArrowRightCircleIcon"
          />
        </button>
      </div>
    </div>

    <vs-prompt
      @accept="calendarOnAccept"
      @cancel="calendarOnCancel"
      @close="calendarOnClose"
      title="航海日と時間を選ぶ"
      accept-text="決定"
      cancel-text="キャンセル"
      :is-valid="validCalendarPicker"
      :active.sync="calendarActive"
    >
      <div class="calendar" style="padding-left: 20px">
        <div class="vx-row">
          <div class="vx-col w-full">
            <label class="vs-input--label">航海日</label>
            <flat-pickr
              v-model="historyDay"
              placeholder="航海日を選択してください"
              :config.sync="configDayPicker"
              style="width: 95%;"
            />
          </div>
        </div>
        <div class="vx-row pt-6 pb-6">
          <div class="vx-col w-full">
            <label class="vs-input--label">航海開始時間(10分単位)</label>
            <flat-pickr
              v-model="historyStartTime"
              placeholder="再生開始時間を選択してください"
              :config.sync="configTimePicker"
              style="width: 95%;"
              :disabled="validTimePicker"
            />
            <div class="vx-col w-full">
              <label v-show="!is10minUnit" class="vs-input--label danger"
                >10分単位で入力してください</label
              >
            </div>
          </div>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import { createNamespacedHelpers } from "vuex";
import { DateUtil } from "@/utils";
// components
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Japanese } from "flatpickr/dist/l10n/ja.js";

// Store
const {
  mapActions: mapActionsOfVesselView,
  mapGetters: mapGettersOfVesselView
} = createNamespacedHelpers("enterpriseVesselView");

export default {
  components: {
    VueSlider,
    flatPickr
  },
  props: {
    vesselId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      show: false,
      isPlayMode: false,
      isDragging: false,
      preSliderValue: null,
      playMode: "live",
      radioColor: "#5097D5",
      sliderDisabled: true,
      sliderValue: [0, 1],
      sliderMarks: null,
      enableMaxTime: [144, 145],
      calendarActive: false,
      currentHistoryDay: null,
      historyDate: null,
      historyDay: null,
      configDayPicker: {
        dateFormat: "Y-m-d",
        time_24hr: true,
        disable: [
          function(date) {
            return date >= new Date();
          }
        ],
        locale: Japanese
      },
      historyStartTime: null,
      configTimePicker: {
        enableTime: true,
        noCalendar: true,
        minuteIncrement: 10,
        time_24hr: true,
        minTime: "00:00",
        maxTime: "23:50",
        locale: Japanese
      },
      formatter: _ => `${this.toolTipFormat()}`
    };
  },
  watch: {
    async playMode(val) {
      if (val === "history") {
        this.isPlayMode = true;
        this.showCalendarPicker();
      } else {
        await this.setVoyageHistoryDateTime(null);
        this.sliderValue = [0, 1];
        this.sliderDisabled = true;
        this.setMode("live");
        this.crateSliderMarks10min();
      }
    },
    historyDay() {
      this.onChangeDayPicker();
    }
  },
  computed: {
    ...mapGettersOfVesselView(["isLiveMode"]),

    validCalendarPicker() {
      let isRequired =
        this.historyDay !== null && this.historyStartTime !== null;
      return isRequired && this.check10min();
    },

    validTimePicker() {
      return this.historyDay === null;
    },

    is10minUnit() {
      return this.check10min();
    },

    isSliderMin() {
      return this.sliderValue[0] <= 0;
    },

    isSliderMax() {
      return this.sliderValue[1] >= 144;
    },
  },
  methods: {
    ...mapActionsOfVesselView(["setVoyageHistoryDateTime", "setMode"]),

    getMarksLabelColor(val) {
      if (this.isLiveMode) {
        return "#e3e3e3";
      } else {
        if (val <= this.enableMaxTime[0]) {
          return "#333333";
        } else {
          return "#e3e3e3";
        }
      }
    },

    crateSliderMarks30Min() {
      let style = {};
      for (let i = 0; i < 49; i++) {
        if (i % 2 === 0) {
          style[i] = {
            label: `${i / 2}:00`,
            labelStyle: {
              color: this.getMarksLabelColor(i)
            }
          };
        } else {
          style[i] = {
            label: ``
          };
        }
      }
      this.sliderMarks = style;
    },

    crateSliderMarks10min() {
      let style = {};
      let count = 0;
      let count2 = 1;
      for (let i = 0; i < 145; i++) {
        if (i % 6 === 0) {
          let hh;
          let h;
          if (count <= 9) {
            hh = "0" + String(count).slice(-2);
            h = String(count);
          } else {
            hh = String(count);
            h = String(count);
          }
          style[i] = {
            label: `${h}:00`,
            labelStyle: {
              color: this.getMarksLabelColor(i)
            },
            sliderValue: [i, i + 1],
            timeValue: `${hh}:00`,
            time: `${hh}:00`
          };
          count++;
        } else {
          let hh;
          if (count - 1 <= 9) {
            hh = "0" + String(count - 1).slice(-2);
          } else {
            hh = String(count - 1);
          }
          let mm = `${count2}0`;
          style[i] = {
            label: ``,
            sliderValue: [i, i + 1],
            timeValue: `${hh}:${mm}`,
            time: `${hh}:${mm}`
          };
          if (count2 === 5) {
            count2 = 1;
          } else {
            count2++;
          }
        }
      }
      this.sliderMarks = style;
    },

    onClickCalendar() {
      this.showCalendarPicker();
    },

    async onChange() {
      if (!this.isDragging && this.preSliderValue !== this.sliderValue) {
        let self = this;
        if (this.enableMaxTime[0] <= this.sliderValue[0]) {
          setTimeout(function() {
            self.sliderValue = self.preSliderValue;
          }, 300);
        } else {
          setTimeout(function() {
            self.updateVoyageHistoryDateTime();
          }, 1000);
          this.preSliderValue = this.sliderValue;
        }
      }
    },
    async onClickPrev() {
      const prevStart = this.sliderValue[0] - 6 >= 0 ? this.sliderValue[0] - 6 : 0;
      this.sliderValue = [prevStart, prevStart + 6];
      await this.onChange();
    },
    async onClickNext() {
      const nextStart = this.sliderValue[0] + 6 <= 144 - 6 ? this.sliderValue[0] + 6 : 144 - 6;
      this.sliderValue = [nextStart, nextStart + 6];
      await this.onChange();
    },

    onDragStart() {
      this.isDragging = true;
    },

    onDragging() {
      this.isDragging = true;
    },

    async onDragEnd() {
      this.isDragging = false;
      if (this.preSliderValue[0] === this.sliderValue[0]) {
        return;
      }
      if (this.enableMaxTime[0] <= this.sliderValue[0]) {
        this.sliderValue = this.preSliderValue;
        return;
      }
      await this.updateVoyageHistoryDateTime();
      this.preSliderValue = this.sliderValue;
    },

    async updateVoyageHistoryDateTime() {
      if (this.isLiveMode) {
        this.setMode("history");
      } else {
        await this.setVoyageHistoryDateTime(null);
      }
      let historyStartTime = this.sliderValueToTime10min(this.sliderValue[0]);
      let startTimeLocal = `${this.historyDay}T${historyStartTime}:00`;
      let startTimeUtc = DateUtil.toMomentObj(startTimeLocal)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss");
      let endTimeUtc = DateUtil.addTime(startTimeUtc, 10, "m").format(
        "YYYY-MM-DDTHH:mm:ss"
      );
      await this.setVoyageHistoryDateTime({
        vesselId: this.vesselId,
        startTimeUtc: `${startTimeUtc}.000Z`,
        endTimeUtc: `${endTimeUtc}.000Z`
      });
    },

    showCalendarPicker() {
      this.calendarActive = true;
      this.historyDay = null;
      this.historyStartTime = null;
    },

    setConfigTimePicker(target) {
      let today = DateUtil.toMomentObj(new Date()).format();
      let diff = DateUtil.diff(target, today, "day");
      if (diff === 0) {
        let nowTime = DateUtil.toMomentObj(new Date()).format("HH:mm:ss");
        let maxTime = DateUtil.rounding10min(nowTime);
        let minTime = "00:00:00";
        this.configTimePicker.minTime = minTime;
        this.configTimePicker.maxTime = maxTime;
        this.historyStartTime = maxTime;
        this.enableMaxTime = this.timeToSliderValue10min(maxTime);
      } else {
        this.configTimePicker.minTime = "00:00:00";
        this.configTimePicker.maxTime = "23:50:00";
        this.historyStartTime = "12:00:00";
        this.enableMaxTime = [144, 145];
      }
    },

    onChangeDayPicker() {
      if (!this.historyDay) return;
      let target = this.historyDay;
      this.setConfigTimePicker(target);
    },

    async calendarOnAccept() {
      if (this.isLiveMode) {
        let startTimeLocal = `${this.historyDay}T${this.historyStartTime}:00`;
        let startTimeUtc = DateUtil.toMomentObj(startTimeLocal)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss");
        let endTimeUtc = DateUtil.addTime(startTimeUtc, 10, "m").format(
          "YYYY-MM-DDTHH:mm:ss"
        );
        await this.setVoyageHistoryDateTime({
          vesselId: this.vesselId,
          startTimeUtc: `${startTimeUtc}.000Z`,
          endTimeUtc: `${endTimeUtc}.000Z`
        });
        this.sliderValue = this.timeToSliderValue10min(this.historyStartTime);
        this.preSliderValue = this.sliderValue;
        this.sliderDisabled = false;
        this.currentHistoryDay = this.historyDay;
        this.setMode("history");
      } else {
        await this.setVoyageHistoryDateTime(null);
        let startTimeLocal = `${this.historyDay}T${this.historyStartTime}:00`;
        let startTimeUtc = DateUtil.toMomentObj(startTimeLocal)
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss");
        let endTimeUtc = DateUtil.addTime(startTimeUtc, 10, "m").format(
          "YYYY-MM-DDTHH:mm:ss"
        );
        await this.setVoyageHistoryDateTime({
          vesselId: this.vesselId,
          startTimeUtc: `${startTimeUtc}.000Z`,
          endTimeUtc: `${endTimeUtc}.000Z`
        });
        this.sliderValue = this.timeToSliderValue10min(this.historyStartTime);
        this.sliderDisabled = false;
        this.currentHistoryDay = this.historyDay;
      }
      this.crateSliderMarks10min();
    },

    timeToSliderValue(time) {
      let splitTime = time.split(":");
      let hh = Number(splitTime[0]) * 2;
      let mm = Number(splitTime[1]);
      if (mm === 30) {
        hh = hh + 1;
      }
      let start = hh;
      return [start, start + 1];
    },

    timeToSliderValue10min(time) {
      let sliderValue;
      Object.keys(this.sliderMarks).some(v => {
        if (this.sliderMarks[v].time === time) {
          sliderValue = this.sliderMarks[v].sliderValue;
          return true;
        }
      });
      return sliderValue;
    },

    sliderValueToTime30min(value) {
      let hh = value;
      let mm = "";
      if (hh % 2 !== 0) {
        mm = "30";
        hh = ("0" + String((value - 1) / 2)).slice(-2);
      } else {
        mm = "00";
        hh = ("0" + String(value / 2)).slice(-2);
      }
      return `${hh}:${mm}`;
    },

    sliderValueToTime10min(value) {
      let time = this.sliderMarks[value].time;
      return time;
    },

    toolTipFormat() {
      if (this.sliderDisabled) return "使用不可";
      let start = this.sliderValueToTime10min(this.sliderValue[0]);
      let end = this.sliderValueToTime10min(this.sliderValue[1]);
      return `${start} ~ ${end}`;
    },

    calendarOnCancel() {
      if (this.isPlayMode) {
        this.playMode = "live";
        this.isPlayMode = false;
      }
    },

    calendarOnClose() {
      if (this.isPlayMode) {
        this.playMode = "live";
        this.isPlayMode = false;
      }
    },

    check30min() {
      if (!this.historyStartTime) return true;
      let min = this.historyStartTime.substr(-2);
      return min === "00" || min === "30";
    },

    check10min() {
      if (!this.historyStartTime) return true;
      let min = this.historyStartTime.substr(-1);
      return min === "0";
    },

    adjustDom() {
      let sliderEl = document.getElementById("timeline-slider");
      let dotEl = sliderEl.getElementsByClassName("vue-slider-dot");
      dotEl[1].childNodes[0].remove("custom-dot");
      let tooltipEl = sliderEl.getElementsByClassName("vue-slider-dot-tooltip");
      tooltipEl[1].childNodes[0].remove("custom-dot");
      this.resizeCustomDotEl();
    },

    adjustSliderMarksDom() {
      let sliderEl = document.getElementById("timeline-slider");
      let sliderMarksEl = sliderEl.getElementsByClassName("vue-slider-marks");
      sliderMarksEl[0].childNodes.forEach( (n, i) => {
        if (i % 6 !== 0) {
          n.childNodes[0].style.height = "14px";
          n.childNodes[0].style.top =  "-10px";
        }
      })
    },

    resizeCustomDotEl() {
      let sliderEl = document.getElementById("timeline-slider");
      let processEl = sliderEl.getElementsByClassName("vue-slider-process");
      let customDotEl = sliderEl.getElementsByClassName("custom-dot");
      customDotEl[0].style.width = `${processEl[0].clientWidth + 2}px`;
    }
  },
  mounted() {
    let self = this;
    window.onresize = function() {
      self.resizeCustomDotEl();
    };
    this.adjustDom();
    this.adjustSliderMarksDom();
  },
  created(){
    this.crateSliderMarks10min();
  },
  beforeDestroy() {
    window.onresize = null;
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#vessel-position-history-timeline-controller {
  .vue-slider-process {
    border-radius: 0px;
  }

  .vue-slider-mark-label {
    top: -55px;
  }

  .vue-slider-marks {
    background-color: #e3e3e3;
  }

  .vue-slider-mark-step {
    height: 24px;
    width: 1px;
    border: 1px solid #e3e3e3;
    border-radius: 0px;
    box-shadow: 0 0 0 0px #e3e3e3;
    background-color: #e3e3e3;
    position: absolute;
    top: -20px;
  }

  .custom-mark {
    position: absolute;
    top: -20px;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  .custom-dot {
    height: 62px;
    width: 100%;
    cursor: pointer;
    background: rgba(45, 156, 219, 0.5);
    border: 2px solid #2d9cdb;
    position: absolute;
    left: 5px;
    bottom: 4px;
    -webkit-transition: border-color 0.3s, -webkit-box-shadow 0.3s;
  }

  #timeline-mode-label {
    margin-left: 15px;
    &.in-vessel-view {
      height: 35px;
    }
    li {
      text-align: center;
      float: left;
      line-height: 50px;
      .live-mode-text {
        margin-right: 10px;
      }
      .history-mode-text {
        margin-left: 10px;
        margin-right: 5px;
      }
      .history-mode-calendar {
        color: #3386dd;
      }
    }
  }

  p {
    position: relative;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    cursor: pointer;
  }

  p.active {
    position: relative;
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    cursor: pointer;
    color: #3386dd;
  }

  p.active:before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -5px;
    display: inline-block;
    width: 40px;
    height: 1px;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    background-color: #3386dd;
    border-radius: 2px;
  }

  .timeline-slider-parent {
    overflow-x: scroll;
    overflow-y: hidden;
    height: 100%;
    position: relative;

    .timeline-slider-items {
      min-width: 1100px;
    }
  }

  #timeline-slider {
    position: absolute;
    width: unquote('max(1000px, 92%) !important');
    bottom: 40px;
  }



  .vue-slider-dot-tooltip-bottom {
    left: 75% !important;
  }
  .timeline-control-button {
    color: $aisea-blue;
    border: none;

    &:active {
      opacity: 0.6;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
</style>
